import React, { useMemo, useState } from "react";
import { createRoot } from "react-dom/client";
import {
  ArrowRight,
  BarChart3,
  Brain,
  CheckCircle2,
  Globe2,
  Heart,
  Search,
  ShoppingBag,
  Sparkles,
  Tag,
  Zap
} from "lucide-react";
import "./styles.css";

type Locale = "zh" | "en";

const products = [
  { id: "p1", name: "通勤降噪耳机 Pro", category: "数码", price: 899, tags: ["通勤", "降噪", "会议"], score: 96 },
  { id: "p2", name: "轻量跑步手表 S2", category: "运动", price: 699, tags: ["跑步", "心率", "户外"], score: 91 },
  { id: "p3", name: "办公室护腰椅 Air", category: "家居", price: 1299, tags: ["办公", "久坐", "人体工学"], score: 94 },
  { id: "p4", name: "便携咖啡机 Mini", category: "生活", price: 399, tags: ["旅行", "咖啡", "便携"], score: 87 },
  { id: "p5", name: "儿童学习平板 Lite", category: "教育", price: 1099, tags: ["儿童", "学习", "家长控制"], score: 89 },
  { id: "p6", name: "宠物自动喂食器 Cam", category: "宠物", price: 499, tags: ["宠物", "摄像头", "远程"], score: 88 }
];

const copy = {
  zh: {
    brand: "启序智选",
    lang: "EN",
    nav: ["商品", "推荐", "运营"],
    label: "独立案例站点 02 / 电商智能推荐",
    title: "一个会解释推荐理由的 AI 电商网站。",
    subtitle:
      "把用户画像、浏览行为、购物车和商品目录合并分析，生成商品推荐、加购组合和运营标签，适合电商导购与会员增长演示。",
    primary: "生成推荐",
    secondary: "查看商品池",
    trust: "适用于品牌商城、私域电商、会员购、跨境独立站",
    productTitle: "精选商品池",
    productSubtitle: "推荐系统只能从真实商品目录中选择，不虚构商品和价格。",
    recoTitle: "AI 推荐工作台",
    recoSubtitle: "输入用户画像或购物意图，后端直连 MiniMax 生成可解释推荐。",
    profileLabel: "用户画像 / 行为",
    profile:
      "用户：一线城市产品经理，最近浏览降噪耳机、人体工学椅和咖啡机；购物车有耳机但未下单；预算 1500 元以内，希望提升通勤和办公效率。",
    run: "生成推荐",
    sample: "填入示例",
    opsTitle: "电商运营视图",
    opsSubtitle: "推荐结果可以直接服务商品排序、优惠券、加购和人群运营。",
    panels: [
      ["推荐策略", "语义意图 + 行为信号 + 商品标签，输出可解释推荐。"],
      ["增长动作", "未下单用户发券，高匹配商品置顶，搭配商品做加购。"],
      ["风控边界", "只推荐目录内商品，不编造库存、价格和促销权益。"]
    ],
    waiting: "等待输入用户画像。",
    footer: "启序科技 AI 案例站点 · 第 2 个真实独立网站"
  },
  en: {
    brand: "Qixu Smart Shop",
    lang: "中文",
    nav: ["Products", "Recommend", "Ops"],
    label: "Standalone Case Site 02 / AI Commerce Recommendation",
    title: "An AI commerce site that explains every recommendation.",
    subtitle:
      "Combine profile, behavior, cart and product catalog signals to generate explainable recommendations, bundles and operation tags.",
    primary: "Generate recommendations",
    secondary: "View catalog",
    trust: "Built for brand stores, private commerce, membership shops and cross-border sites",
    productTitle: "Curated Product Catalog",
    productSubtitle: "The system can only recommend real catalog items, without inventing products or prices.",
    recoTitle: "AI Recommendation Desk",
    recoSubtitle: "Enter user intent or behavior. The server calls MiniMax directly for grounded recommendations.",
    profileLabel: "User profile / behavior",
    profile:
      "User: product manager in a tier-one city. Recently viewed noise-cancelling headphones, ergonomic chairs and coffee machines. Headphones are in cart but not purchased. Budget under 1500 RMB, wants better commute and work efficiency.",
    run: "Recommend",
    sample: "Use sample",
    opsTitle: "Commerce Ops View",
    opsSubtitle: "Recommendation output can drive ranking, coupons, bundling and audience operations.",
    panels: [
      ["Strategy", "Semantic intent + behavior signals + product tags with explainable output."],
      ["Growth action", "Coupon for abandoned cart, rank high-match items, bundle add-ons."],
      ["Guardrails", "Only catalog products, no invented stock, prices or promotion rights."]
    ],
    waiting: "Waiting for a user profile.",
    footer: "Qixu Technology AI case site · second real standalone website"
  }
};

function App() {
  const [locale, setLocale] = useState<Locale>("zh");
  const t = copy[locale];
  const [profile, setProfile] = useState(t.profile);
  const [result, setResult] = useState(t.waiting);
  const [meta, setMeta] = useState("");
  const [loading, setLoading] = useState(false);
  const [liked, setLiked] = useState<Record<string, boolean>>({});

  const ranked = useMemo(
    () => [...products].sort((a, b) => b.score - a.score),
    []
  );

  function switchLocale() {
    const next = locale === "zh" ? "en" : "zh";
    setLocale(next);
    setProfile(copy[next].profile);
    setResult(copy[next].waiting);
    setMeta("");
  }

  async function recommend() {
    const value = profile.trim();
    if (!value || loading) return;
    setLoading(true);
    setResult(locale === "zh" ? "正在请求 MiniMax 生成推荐..." : "Requesting MiniMax recommendations...");
    setMeta("");
    try {
      const response = await fetch("/api/recommend", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ profile: value, locale })
      });
      const data = await response.json();
      setResult(data.text || t.waiting);
      setMeta(`${data.provider || "unknown"} / ${data.model || "unknown"}`);
    } catch {
      setResult(locale === "zh" ? "网络异常，已无法生成推荐。" : "Network error. Recommendation unavailable.");
      setMeta("network fallback");
    } finally {
      setLoading(false);
    }
  }

  return (
    <main>
      <header className="topbar">
        <a className="brand" href="#top">
          <span>选</span>
          <strong>{t.brand}</strong>
        </a>
        <nav>
          <a href="#products">{t.nav[0]}</a>
          <a href="#recommend">{t.nav[1]}</a>
          <a href="#ops">{t.nav[2]}</a>
        </nav>
        <button className="lang" onClick={switchLocale}>
          <Globe2 size={16} />
          {t.lang}
        </button>
      </header>

      <section className="hero" id="top">
        <div>
          <p className="eyebrow"><Sparkles size={16} />{t.label}</p>
          <h1>{t.title}</h1>
          <p className="subtitle">{t.subtitle}</p>
          <div className="actions">
            <a className="button primary" href="#recommend">{t.primary}<ArrowRight size={18} /></a>
            <a className="button secondary" href="#products">{t.secondary}</a>
          </div>
          <p className="trust">{t.trust}</p>
        </div>
        <div className="hero-board">
          <div className="board-head">
            <Brain size={22} />
            <span>Recommendation engine</span>
          </div>
          <div className="funnel">
            <div><Search size={18} /> 浏览行为</div>
            <div><ShoppingBag size={18} /> 购物车</div>
            <div><Tag size={18} /> 商品标签</div>
            <div><Zap size={18} /> 推荐理由</div>
          </div>
          <div className="hero-pick">
            <strong>Top match</strong>
            <span>通勤降噪耳机 Pro</span>
            <em>匹配度 96 / 加购建议：护腰椅 Air</em>
          </div>
        </div>
      </section>

      <section className="catalog" id="products">
        <div className="section-copy">
          <h2>{t.productTitle}</h2>
          <p>{t.productSubtitle}</p>
        </div>
        <div className="product-grid">
          {ranked.map((item) => (
            <article key={item.id} className="product-card">
              <div className="product-art">
                <ShoppingBag size={34} />
                <button
                  aria-label={`like ${item.name}`}
                  onClick={() => setLiked((state) => ({ ...state, [item.id]: !state[item.id] }))}
                >
                  <Heart size={18} fill={liked[item.id] ? "currentColor" : "none"} />
                </button>
              </div>
              <div className="product-meta">
                <span>{item.category}</span>
                <strong>{item.name}</strong>
                <p>¥{item.price}</p>
              </div>
              <div className="tags">
                {item.tags.map((tag) => <span key={tag}>{tag}</span>)}
              </div>
              <div className="score">
                <span>AI match</span>
                <strong>{item.score}</strong>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="reco" id="recommend">
        <div className="desk">
          <div className="section-title">
            <Brain size={22} />
            <div>
              <h2>{t.recoTitle}</h2>
              <p>{t.recoSubtitle}</p>
            </div>
          </div>
          <label>
            {t.profileLabel}
            <textarea value={profile} onChange={(event) => setProfile(event.target.value)} />
          </label>
          <div className="composer-actions">
            <button onClick={() => setProfile(t.profile)}>{t.sample}</button>
            <button className="primary" onClick={recommend} disabled={loading || !profile.trim()}>
              {t.run}<Sparkles size={16} />
            </button>
          </div>
        </div>
        <div className="result-card" aria-live="polite">
          <div className="result-head">
            <CheckCircle2 size={20} />
            <strong>AI output</strong>
          </div>
          <p>{result}</p>
          {meta && <span>{meta}</span>}
        </div>
      </section>

      <section className="ops" id="ops">
        <div>
          <h2>{t.opsTitle}</h2>
          <p>{t.opsSubtitle}</p>
        </div>
        <div className="stats">
          <div><span>CTR</span><strong>18.4%</strong><em>+4.2%</em></div>
          <div><span>加购率</span><strong>9.8%</strong><em>+2.1%</em></div>
          <div><span>客单价</span><strong>¥842</strong><em>+13%</em></div>
        </div>
        <div className="ops-grid">
          {t.panels.map(([title, content]) => (
            <article key={title}>
              <BarChart3 size={20} />
              <h3>{title}</h3>
              <p>{content}</p>
            </article>
          ))}
        </div>
      </section>

      <footer>{t.footer}</footer>
    </main>
  );
}

createRoot(document.getElementById("root")!).render(<App />);
